<template>
  <div class="uncleared-checks-panel">
    <div class="uc-header">
      <h2>Uncleared Checks</h2>
      <div class="uc-controls">
        <label>As of Date:</label>
        <input type="date" v-model="asOfDate" @change="fetchData" />
        <span class="uc-count" v-if="checks.length">
          {{ checks.length }} checks &bull; Total:
          {{ formatCurrency(totalAmount) }}
        </span>
      </div>
      <button class="uc-close-btn" @click="$emit('close')" title="Close">
        <i class="fas fa-times"></i>
      </button>
    </div>
    <div class="uc-body">
      <div v-if="loading" class="uc-loading">Loading...</div>
      <div v-else-if="error" class="uc-error">{{ error }}</div>
      <div v-else-if="checks.length === 0" class="uc-empty">
        No uncleared checks found for {{ formatDisplayDate(asOfDate) }}.
      </div>
      <table v-else class="uc-table">
        <thead>
          <tr>
            <th>Ledger</th>
            <th>Bank</th>
            <th class="narrow-col">Type</th>
            <th>Bank Check</th>
            <th class="right-align">Credit</th>
            <th class="right-align">Debit</th>
            <th class="narrow-col">Type</th>
            <th>Check No.</th>
            <th>Description</th>
            <th>Account</th>
            <th>Class</th>
            <th>Memo</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(check, index) in checks"
            :key="check.Id"
            :class="{ even: index % 2 === 0, odd: index % 2 !== 0 }"
          >
            <td>{{ check.ledgerDate }}</td>
            <td>{{ check.BankRefDateTime }}</td>
            <td class="center-align">{{ check.BankType }}</td>
            <td class="center-align">{{ check.BankRefCheckNum }}</td>
            <td class="right-align">{{ formatNumber(check.Credit) }}</td>
            <td class="right-align">{{ formatNumber(check.Debit) }}</td>
            <td class="center-align">{{ check.Type }}</td>
            <td class="center-align">{{ check.checkNumber }}</td>
            <td class="text-ellipsis">{{ check.Description }}</td>
            <td class="text-ellipsis">{{ check.Account }}</td>
            <td class="text-ellipsis-narrow">{{ check.Class }}</td>
            <td class="text-ellipsis-narrow">{{ check.Memo }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import axios from '@/axiosConfig';

export default {
  name: 'UnclearedChecks',
  emits: ['close'],
  data() {
    const now = new Date();
    const priorYearEnd = `${now.getFullYear() - 1}-12-31`;
    return {
      asOfDate: priorYearEnd,
      checks: [],
      loading: false,
      error: '',
    };
  },
  computed: {
    totalAmount() {
      return this.checks.reduce((sum, c) => {
        const debit = parseFloat(c.Debit) || 0;
        const credit = parseFloat(c.Credit) || 0;
        return sum + debit - credit;
      }, 0);
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      this.error = '';
      try {
        const response = await axios.get('/api/uncleared-checks', {
          params: { asOfDate: this.asOfDate },
        });
        this.checks = response.data;
      } catch (err) {
        console.error('Error fetching uncleared checks:', err);
        this.error = 'Failed to load uncleared checks.';
      } finally {
        this.loading = false;
      }
    },
    formatNumber(value) {
      if (value === null || value === undefined || value === '') return '';
      const num = parseFloat(value);
      if (isNaN(num) || num === 0) return '';
      return num.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatCurrency(value) {
      const num = Math.abs(value);
      return (
        '$' +
        num.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      );
    },
    formatDisplayDate(dateStr) {
      if (!dateStr) return '';
      const [y, m, d] = dateStr.split('-');
      return `${parseInt(m)}/${parseInt(d)}/${y.slice(2)}`;
    },
  },
};
</script>

<style scoped>
.uncleared-checks-panel {
  background: white;
  border-radius: 12px;
  width: 95vw;
  max-width: 1400px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.uc-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  border-bottom: 1px solid #e0d8e3;
  background: #f5f0f7;
  border-radius: 12px 12px 0 0;
  flex-wrap: wrap;
}

.uc-header h2 {
  margin: 0;
  font-size: 1.2rem;
  color: #4a3555;
  white-space: nowrap;
}

.uc-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.uc-controls label {
  font-size: 0.85rem;
  color: #666;
  white-space: nowrap;
}

.uc-controls input[type='date'] {
  padding: 4px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.85rem;
}

.uc-count {
  font-size: 0.85rem;
  color: #666;
  margin-left: 12px;
  white-space: nowrap;
}

.uc-close-btn {
  background: none;
  border: none;
  font-size: 1.2rem;
  color: #999;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  margin-left: auto;
}

.uc-close-btn:hover {
  background: #eee;
  color: #333;
}

.uc-body {
  overflow: auto;
  flex: 1;
  padding: 0;
}

.uc-loading,
.uc-error,
.uc-empty {
  text-align: center;
  padding: 40px 20px;
  color: #666;
  font-size: 0.95rem;
}

.uc-error {
  color: #c0392b;
}

.uc-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
}

.uc-table thead {
  position: sticky;
  top: 0;
  z-index: 1;
}

.uc-table th {
  background: #6e5875;
  color: white;
  padding: 8px 10px;
  text-align: left;
  white-space: nowrap;
  font-weight: 600;
  font-size: 0.8rem;
}

.uc-table td {
  padding: 6px 10px;
  border-bottom: 1px solid #eee;
  white-space: nowrap;
}

.uc-table .even {
  background: #fafafa;
}

.uc-table .odd {
  background: #fff;
}

.uc-table tr:hover {
  background: #f0ebf3;
}

.right-align {
  text-align: right;
}

.center-align {
  text-align: center;
}

.narrow-col {
  width: 60px;
}

.text-ellipsis {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.text-ellipsis-narrow {
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 768px) {
  .uncleared-checks-panel {
    width: 100vw;
    max-width: 100vw;
    max-height: 100vh;
    border-radius: 0;
  }

  .uc-header {
    border-radius: 0;
  }

  .uc-table {
    font-size: 0.75rem;
  }
}
</style>
